import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Input, InputNumber, Checkbox, Button, Alert, Spin, message } from 'antd';
import moment from 'moment';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '@skygroup/shared/API/endpoints';
import { RootState } from '../../store/store';
import {
  SupplierApprovalData,
  SupplierApprovalResponse,
  SupplierApprovalFlightLeg,
  RoomInputState,
  getGuestName,
} from './supplierApprovalTypes';
import './SupplierApprovalPage.less';

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const m = moment(value, 'DD/MM/YYYY', true);
  return m.isValid() ? m.format('DD MMM YYYY') : value;
};

const formatDateTime = (value?: string): string => {
  if (!value) return '';
  const m = moment(value);
  return m.isValid() ? m.format('DD MMM YYYY, HH:mm') : value;
};

const formatFlightLeg = (leg?: SupplierApprovalFlightLeg): string => {
  if (!leg) return '';
  const route =
    leg.fromCode || leg.fromName || leg.toCode || leg.toName
      ? `${leg.fromCode ?? leg.fromName ?? ''} → ${leg.toCode ?? leg.toName ?? ''}`
      : '';
  return [
    leg.flightNumber ? `Flight ${leg.flightNumber}` : '',
    leg.airlineName,
    route,
    leg.dateTime,
  ]
    .filter(Boolean)
    .join(' · ');
};

const defaultRoomInput = (): RoomInputState => ({
  ts_pnr: '',
  priceMode: 'approve',
  suggested_supplier_net_price: '',
  action: undefined,
});

const SupplierApprovalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const supplierId = searchParams.get('supplier_id');
  const token = searchParams.get('token');

  const { translations, direction } = useSelector(
    (state: RootState) => state.config
  );
  const t: any = translations?.supplierApproval || {};

  const [data, setData] = useState<SupplierApprovalData | null>(null);
  const [inputs, setInputs] = useState<Record<number, RoomInputState>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [alreadyApproved, setAlreadyApproved] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    message?: string;
    approved_at?: string;
    approved_product_ids?: number[];
    approvedCount?: number;
    notApprovedCount?: number;
    priceProposedCount?: number;
  } | null>(null);

  const seedInputs = useCallback((d: SupplierApprovalData) => {
    const seeded: Record<number, RoomInputState> = {};
    d.rooms?.forEach((room) => {
      seeded[room.orderProductId] = defaultRoomInput();
    });
    setInputs(seeded);
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setAlreadyApproved(false);

    try {
      const res = await apiClient.get(endpoints.SUPPLIER_APPROVAL.GET, {
        params: { order_id: orderId, supplier_id: supplierId, token },
      });
      const body: SupplierApprovalResponse = res?.data;

      if (body?.status === 'error') {
        setLoadError(body.message || t.errorBanner || 'An error occurred.');
        setLoading(false);
        return;
      }

      const d = body?.data;
      if (!d) {
        setLoadError(t.errorBanner || 'An error occurred. Please try again.');
        setLoading(false);
        return;
      }

      if (
        body?.status === 'already_approved' ||
        body?.already_approved ||
        d.already_approved ||
        d.status === 'already_approved'
      ) {
        setAlreadyApproved(true);
      }

      setData(d);
      seedInputs(d);
    } catch {
      setLoadError(t.errorBanner || 'An error occurred. Please try again.');
    }
    setLoading(false);
  }, [orderId, supplierId, token, seedInputs, t.errorBanner]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const rooms = data?.rooms ?? [];
  const currency = rooms[0]?.currencySign ?? '';

  // Group rooms by hotel so each hotel's name shows once above its own
  // rooms instead of repeating per row — a supplier can have rooms across
  // more than one hotel in the same approval (see multi-supplier grouping).
  // With a single room there's nothing to group, so keep the plain inline
  // hotel line instead of a redundant one-room section header.
  const roomGroups = useMemo(() => {
    if (rooms.length <= 1) {
      return [{ hotelName: null as string | null, rooms }];
    }
    const order: string[] = [];
    const byHotel = new Map<string, typeof rooms>();
    const NO_HOTEL = '__no-hotel';
    rooms.forEach((room) => {
      const key = room.hotelName || NO_HOTEL;
      if (!byHotel.has(key)) {
        byHotel.set(key, []);
        order.push(key);
      }
      byHotel.get(key)!.push(room);
    });
    return order.map((key) => ({
      hotelName: key === NO_HOTEL ? null : key,
      rooms: byHotel.get(key)!,
    }));
  }, [rooms]);

  // Room numbering stays continuous across hotel groups rather than
  // resetting to 1 per group, so two rooms never show the same badge.
  const globalRoomIndex = useMemo(
    () => new Map(rooms.map((room, i) => [room.orderProductId, i])),
    [rooms]
  );

  // Total starts as the full sum of every room's net price and drops a room's
  // price when it's marked "not approved" (which now includes rooms where the
  // supplier suggested a different price).
  const totalExpectedNet = useMemo(
    () =>
      rooms.reduce((acc, room) => {
        if (inputs[room.orderProductId]?.action === 'not_approved') return acc;
        return acc + room.supplierNetPrice * (room.amount ?? 1);
      }, 0),
    [rooms, inputs]
  );

  const approvedCount = useMemo(
    () => Object.values(inputs).filter((i) => i.action === 'approved').length,
    [inputs]
  );

  const updateRoom = (roomId: number, patch: Partial<RoomInputState>) => {
    setInputs((prev) => ({
      ...prev,
      [roomId]: { ...(prev[roomId] ?? defaultRoomInput()), ...patch },
    }));
  };

  const applyPnrToAll = (roomId: number) => {
    const value = inputs[roomId]?.ts_pnr?.trim() ?? '';
    if (!value) return; // can't apply an empty PNR
    setInputs((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[Number(k)] = { ...next[Number(k)], ts_pnr: value };
      });
      return next;
    });
    message.success(t.appliedToAll || 'Applied to all rooms');
  };

  const applyPriceToAll = (roomId: number) => {
    const src = inputs[roomId];
    const value = src?.suggested_supplier_net_price ?? '';
    // Only a valid different price can be applied to all rooms.
    if (
      !src ||
      src.priceMode !== 'different' ||
      value === '' ||
      Number.isNaN(Number(value)) ||
      Number(value) <= 0
    ) {
      return;
    }
    setInputs((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[Number(k)] = {
          ...next[Number(k)],
          priceMode: 'different',
          suggested_supplier_net_price: value,
          // A different price marks the room not approved — apply that too.
          action: 'not_approved',
        };
      });
      return next;
    });
    message.success(t.appliedToAll || 'Applied to all rooms');
  };

  // Approving accepts the current price, so it clears any "different price".
  const approveRoom = (roomId: number) => {
    const input = inputs[roomId] ?? defaultRoomInput();
    if (input.action === 'approved') {
      updateRoom(roomId, { action: undefined });
      return;
    }
    if (!input.ts_pnr.trim()) {
      message.warning(
        t.pnrRequiredMsg ||
          'Please enter the supplier PNR for this room before approving.'
      );
      return;
    }
    updateRoom(roomId, {
      action: 'approved',
      priceMode: 'approve',
      suggested_supplier_net_price: '',
    });
  };

  const rejectRoom = (roomId: number) => {
    const input = inputs[roomId] ?? defaultRoomInput();
    if (input.action === 'not_approved') {
      // Toggle back to pending and drop any suggested price.
      updateRoom(roomId, {
        action: undefined,
        priceMode: 'approve',
        suggested_supplier_net_price: '',
      });
      return;
    }
    updateRoom(roomId, { action: 'not_approved' });
  };

  // Suggesting a different price automatically marks the room as not approved.
  const toggleDifferentPrice = (roomId: number, checked: boolean) => {
    if (checked) {
      updateRoom(roomId, { priceMode: 'different', action: 'not_approved' });
    } else {
      updateRoom(roomId, {
        priceMode: 'approve',
        suggested_supplier_net_price: '',
        action: undefined,
      });
    }
  };

  const validateRooms = (): string | null => {
    for (const room of rooms) {
      const input = inputs[room.orderProductId];
      if (!input?.action) continue;
      // PNR is required to approve, optional when not approving.
      if (input.action === 'approved' && !input.ts_pnr.trim()) {
        return t.errorPnrRequired || 'Supplier PNR is required for approved rooms.';
      }
      // If they suggested a different price, it must be a valid number.
      if (input.action === 'not_approved' && input.priceMode === 'different') {
        const val = Number(input.suggested_supplier_net_price);
        if (!input.suggested_supplier_net_price || Number.isNaN(val) || val <= 0) {
          return (
            t.errorSuggestedPrice ||
            'Please enter a valid suggested supplier net price.'
          );
        }
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setErrorMsg(null);
    const validationError = validateRooms();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    // Approved rooms → approve API (id + required PNR).
    const approvedItems = rooms
      .filter((room) => inputs[room.orderProductId]?.action === 'approved')
      .map((room) => ({
        order_product_id: room.orderProductId,
        ts_pnr: inputs[room.orderProductId]!.ts_pnr.trim(),
      }));

    // Not-approved rooms → not-approve API (id + optional price + optional PNR).
    const notApprovedItems = rooms
      .filter((room) => inputs[room.orderProductId]?.action === 'not_approved')
      .map((room) => {
        const input = inputs[room.orderProductId]!;
        const item: {
          order_product_id: number;
          suggested_supplier_net_price?: number;
          ts_pnr?: string;
        } = { order_product_id: room.orderProductId };
        if (
          input.priceMode === 'different' &&
          input.suggested_supplier_net_price !== ''
        ) {
          item.suggested_supplier_net_price = Number(
            input.suggested_supplier_net_price
          );
        }
        if (input.ts_pnr.trim()) {
          item.ts_pnr = input.ts_pnr.trim();
        }
        return item;
      });

    // Call whichever API has items — both when the order is mixed.
    const calls: Promise<any>[] = [];
    if (approvedItems.length) {
      calls.push(
        apiClient.post(endpoints.SUPPLIER_APPROVAL.APPROVE, {
          order_id: orderId,
          supplier_id: supplierId,
          token,
          items: approvedItems,
        })
      );
    }
    if (notApprovedItems.length) {
      calls.push(
        apiClient.post(endpoints.SUPPLIER_APPROVAL.NOT_APPROVE, {
          order_id: orderId,
          supplier_id: supplierId,
          token,
          items: notApprovedItems,
        })
      );
    }

    const priceProposedCount = notApprovedItems.filter(
      (i) => i.suggested_supplier_net_price != null
    ).length;
    const notApprovedCount = notApprovedItems.length - priceProposedCount;

    setSubmitting(true);
    try {
      const responses = await Promise.all(calls);
      const errored = responses.find((r) => r?.data?.status === 'error');
      if (errored) {
        setErrorMsg(
          errored.data?.message || t.errorBanner || 'An error occurred.'
        );
        setSubmitting(false);
        return;
      }
      const resultData = responses
        .map((r) => r?.data?.data)
        .find((d) => d?.approved_at || d?.message);
      setSubmitResult({
        ...(resultData ?? {}),
        approvedCount: approvedItems.length,
        notApprovedCount,
        priceProposedCount,
      });
      setSubmitted(true);
    } catch {
      setErrorMsg(t.errorBanner || 'An error occurred. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Loading / error / already-approved / submitted states ───────────────

  if (loading) {
    return (
      <div className="supplier-approval-page">
        <div className="sap-status">
          <Spin size="large" />
          <p>{t.loading || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="supplier-approval-page">
        <div className="sap-status">
          <div className="sap-status-icon error">⚠</div>
          <h2>{t.errorTitle || 'Unable to load'}</h2>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="supplier-approval-page">
        <div className="sap-status">
          <p>{t.errorBanner || 'An error occurred. Please try again.'}</p>
        </div>
      </div>
    );
  }

  if (alreadyApproved) {
    const orderRef = data?.order_number ?? data?.order_id;
    return (
      <div className="supplier-approval-page">
        <div className="sap-status sap-status-done">
          <div className="sap-status-icon success">✓</div>
          <span className="sap-status-badge">{t.completedBadge || 'Completed'}</span>
          <h2>{t.alreadyApprovedTitle || 'Already reviewed'}</h2>
          <p>
            {t.alreadyApprovedMsg ||
              'This reservation request has already been reviewed. No further action is needed.'}
          </p>
          {orderRef != null && (
            <div className="sap-status-order">
              {t.orderLabel || 'Order'} #{orderRef}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    const approvedAt = formatDateTime(submitResult?.approved_at);
    const {
      approvedCount = 0,
      notApprovedCount = 0,
      priceProposedCount = 0,
    } = submitResult ?? {};
    const decisionKinds = [
      approvedCount > 0,
      notApprovedCount > 0,
      priceProposedCount > 0,
    ].filter(Boolean).length;

    let title = t.submittedTitle || 'Reservation approved';
    let message =
      submitResult?.message ||
      t.submittedMsg ||
      'The reservation has been approved successfully.';

    if (decisionKinds > 1) {
      // Mixed outcome (e.g. one room approved, another declined or price-disputed)
      // — the single approve/not-approve response text no longer applies to the
      // whole submission, so summarize what actually happened per room instead.
      title = t.submittedMixedTitle || 'Response submitted';
      const parts: string[] = [];
      if (approvedCount) {
        parts.push(
          `${approvedCount} ${
            approvedCount > 1
              ? t.roomsApproved || 'rooms approved'
              : t.roomApproved || 'room approved'
          }`
        );
      }
      if (notApprovedCount) {
        parts.push(
          `${notApprovedCount} ${
            notApprovedCount > 1
              ? t.roomsNotApproved || 'rooms not approved'
              : t.roomNotApproved || 'room not approved'
          }`
        );
      }
      if (priceProposedCount) {
        parts.push(
          `${priceProposedCount} ${
            priceProposedCount > 1
              ? t.roomsPriceProposed || 'price changes proposed for review'
              : t.roomPriceProposed || 'price change proposed for review'
          }`
        );
      }
      message = `${parts.join(', ')}.`;
    } else if (notApprovedCount > 0) {
      title = t.submittedNotApprovedTitle || 'Reservation not approved';
      message =
        submitResult?.message ||
        t.submittedNotApprovedMsg ||
        'The reservation has been declined.';
    } else if (priceProposedCount > 0) {
      title = t.submittedPriceProposedTitle || 'Price proposal submitted';
      message =
        submitResult?.message ||
        t.submittedPriceProposedMsg ||
        'Your suggested price has been submitted for review.';
    }

    return (
      <div className="supplier-approval-page">
        <div className="sap-status">
          <div className="sap-status-icon success">✓</div>
          <h2>{title}</h2>
          <p>{message}</p>
          {approvedAt && (
            <p className="sap-status-meta">
              {t.approvedOn || 'Approved on'}: {approvedAt}
            </p>
          )}
        </div>
      </div>
    );
  }

  const totalRooms = rooms.length;
  const hasPending = rooms.some((room) => !inputs[room.orderProductId]?.action);
  const submitDisabled = hasPending || submitting || totalRooms === 0;

  // All products in the order share the same stay, so surface it once in the
  // header instead of repeating a per-room column.
  const stayRoom = rooms[0];

  return (
    <div className="supplier-approval-page" dir={direction || 'rtl'}>
      <div className="sap-container">
        {/* Order meta bar */}
        <div className="sap-order-bar">
          <div className="sap-order-meta">
            <span className="sap-meta-item">
              <span className="sap-meta-label">{t.orderLabel || 'Order'}</span>
              <span className="sap-meta-value">#{data.order_number}</span>
            </span>
            {data.supplier_name && (
              <span className="sap-meta-item">
                <span className="sap-meta-label">{t.supplierLabel || 'Supplier'}</span>
                <span className="sap-meta-value">{data.supplier_name}</span>
              </span>
            )}
            {data.customer_phone && (
              <span className="sap-meta-item">
                <span className="sap-meta-label">
                  {t.customerPhoneLabel || 'Client phone number'}
                </span>
                <span className="sap-meta-value">{data.customer_phone}</span>
              </span>
            )}
            {stayRoom && (
              <>
                <span className="sap-meta-item">
                  <span className="sap-meta-label">{t.checkIn || 'Check-in'}</span>
                  <span className="sap-meta-value">
                    {formatDate(stayRoom.checkIn)}
                  </span>
                </span>
                <span className="sap-meta-item">
                  <span className="sap-meta-label">{t.checkOut || 'Check-out'}</span>
                  <span className="sap-meta-value">
                    {formatDate(stayRoom.checkOut)}
                  </span>
                </span>
                <span className="sap-meta-item">
                  <span className="sap-meta-label">{t.colStay || 'Stay'}</span>
                  <span className="sap-meta-value">
                    {stayRoom.nights} {t.nights || 'nights'}
                  </span>
                </span>
              </>
            )}
          </div>
          <div className="sap-approved-badge">
            {approvedCount} / {totalRooms} {t.approvedSuffix || 'rooms approved'}
          </div>
        </div>

        {errorMsg && (
          <Alert
            className="sap-error"
            type="error"
            message={errorMsg}
            showIcon
            closable
            onClose={() => setErrorMsg(null)}
          />
        )}

        {/* Only shown when this supplier also handles the transfer — they
            need the guest's flight times to plan pickup/drop-off. */}
        {(data.flightDetails?.arrivalFlight ||
          data.flightDetails?.departureFlight ||
          data.flightDetails?.transfer?.pickupDateTime ||
          data.flightDetails?.transfer?.dropoffDateTime) && (
          <div className="sap-flight-details">
            <div className="sap-flight-details-title">
              ✈️ {t.flightDetailsTitle || 'Flight & Transfer Details'}
            </div>
            {data.flightDetails?.arrivalFlight && (
              <div className="sap-flight-line">
                <span className="sap-flight-key">
                  {t.arrivalFlight || 'Arrival'}
                </span>
                {formatFlightLeg(data.flightDetails.arrivalFlight)}
              </div>
            )}
            {data.flightDetails?.departureFlight && (
              <div className="sap-flight-line">
                <span className="sap-flight-key">
                  {t.departureFlight || 'Departure'}
                </span>
                {formatFlightLeg(data.flightDetails.departureFlight)}
              </div>
            )}
            {data.flightDetails?.transfer?.pickupDateTime && (
              <div className="sap-flight-line">
                <span className="sap-flight-key">
                  {t.transferPickup || 'Transfer pickup'}
                </span>
                {data.flightDetails.transfer.pickupDateTime}
              </div>
            )}
            {data.flightDetails?.transfer?.dropoffDateTime && (
              <div className="sap-flight-line">
                <span className="sap-flight-key">
                  {t.transferDropoff || 'Transfer drop-off'}
                </span>
                {data.flightDetails.transfer.dropoffDateTime}
              </div>
            )}
          </div>
        )}

        {/* Elevated table card */}
        <div className="sap-table">
          {/* Column header row */}
          <div className="sap-grid sap-table-head">
            <div className="sap-th">{t.colRoom || 'Room'}</div>
            <div className="sap-th">{t.colGuests || 'Guests'}</div>
            <div className="sap-th">{t.colNotes || 'Notes'}</div>
            <div className="sap-th">{t.colPnr || 'Supplier PNR'}</div>
            <div className="sap-th">{t.colNetPrice || 'Your Net Price'}</div>
            <div className="sap-th">{t.colAction || 'Action'}</div>
          </div>

          {roomGroups.map((group, groupIdx) => (
            <React.Fragment key={group.hotelName ?? `ungrouped-${groupIdx}`}>
              {group.hotelName && (
                <div className="sap-hotel-row">🏨 {group.hotelName}</div>
              )}
              {group.rooms.map((room) => {
                const idx = globalRoomIndex.get(room.orderProductId) ?? 0;
                const input = inputs[room.orderProductId] ?? defaultRoomInput();
                const isApproved = input.action === 'approved';
                const isNotApproved = input.action === 'not_approved';
                const isDifferent = input.priceMode === 'different';
                const suggestedNum = Number(input.suggested_supplier_net_price);
                const priceDiff =
                  isDifferent && input.suggested_supplier_net_price !== ''
                    ? suggestedNum - room.supplierNetPrice
                    : null;
                const roomCurrency = room.currencySign || currency;

                return (
                  <div
                    className={`sap-grid sap-room-row${
                      isApproved ? ' is-approved' : ''
                    }${isNotApproved ? ' is-not-approved' : ''}`}
                    key={room.orderProductId}
                  >
                    {/* Room */}
                    <div className="sap-cell sap-cell-room">
                      <div className="sap-cell-label">{t.colRoom || 'Room'}</div>
                      <div className="sap-room-head">
                        <span className="sap-room-index">{idx + 1}</span>
                        <span className="sap-room-name">{room.roomName}</span>
                      </div>
                      {room.hotelName && !group.hotelName && (
                        <div className="sap-room-hotel">🏨 {room.hotelName}</div>
                      )}
                      <div className="sap-room-sub">
                        {room.board && (
                          <span>
                            {t.board || 'Board'}: {room.board}
                          </span>
                        )}
                        {room.amount ? (
                          <span>
                            {room.amount} {t.roomsUnit || 'room(s)'}
                          </span>
                        ) : null}
                      </div>
                      {room.paxSummary && (
                        <div className="sap-pax">{room.paxSummary}</div>
                      )}
                      {isApproved && (
                        <span className="sap-room-status approved">
                          ✓ {t.approved || 'Approved'}
                        </span>
                      )}
                      {isNotApproved && (
                        <span className="sap-room-status rejected">
                          ✕ {t.notApproved || 'Not Approved'}
                        </span>
                      )}
                    </div>

                    {/* Guests */}
                    <div className="sap-cell sap-cell-guests">
                      <div className="sap-cell-label">{t.colGuests || 'Guests'}</div>
                      {room.guests?.length ? (
                        <ul className="sap-guest-list">
                          {room.guests.map((g, gi) => (
                            <li key={gi}>
                              <span className="sap-guest-name">
                                {getGuestName(g) || (t.guestUnnamed || 'Guest')}
                              </span>
                              <span className="sap-guest-type">
                                {' '}
                                — {t[g.type] || g.type}
                                {g.age != null ? `, ${g.age}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="sap-muted">—</span>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="sap-cell sap-cell-notes">
                      <div className="sap-cell-label">{t.colNotes || 'Notes'}</div>
                      {data.notes ? (
                        <div className="sap-note">{data.notes}</div>
                      ) : (
                        <span className="sap-muted">{t.noNotes || 'No notes'}</span>
                      )}
                    </div>

                    {/* Supplier PNR */}
                    <div className="sap-cell sap-cell-pnr">
                      <div className="sap-cell-label">{t.colPnr || 'Supplier PNR'}</div>
                      <Input
                        className="sap-pnr-input"
                        placeholder={t.pnrPlaceholder || 'Enter PNR'}
                        value={input.ts_pnr}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Clearing the PNR can't leave the room approved.
                          updateRoom(room.orderProductId, {
                            ts_pnr: value,
                            ...(!value.trim() && input.action === 'approved'
                              ? { action: undefined }
                              : {}),
                          });
                        }}
                      />
                      <div className="sap-pnr-hint">
                        {t.pnrRequired || 'Required to approve'}
                      </div>
                      <button
                        type="button"
                        className="sap-link"
                        disabled={!input.ts_pnr.trim()}
                        onClick={() => applyPnrToAll(room.orderProductId)}
                      >
                        {t.applyToAll || 'Apply to all rooms'}
                      </button>
                    </div>

                    {/* Net price + price approval */}
                    <div className="sap-cell sap-cell-price">
                      <div className="sap-cell-label">
                        {t.colNetPrice || 'Your Net Price'}
                      </div>
                      <div className={`sap-price${isDifferent ? ' struck' : ''}`}>
                        {room.supplierNetPrice} {roomCurrency}
                      </div>
                      <div className="sap-price-expected">
                        {t.expectedNet || 'Expected net'} · {roomCurrency}
                      </div>

                      {isDifferent && (
                        <div className="sap-suggested">
                          <div className="sap-suggested-label">
                            {t.suggestedPrice || 'Suggested net price'}
                          </div>
                          <InputNumber
                            className="sap-suggested-input"
                            min={0}
                            precision={0}
                            controls={false}
                            inputMode="numeric"
                            // Digits only: allow control keys / shortcuts, block any
                            // other single-character key that isn't 0-9.
                            onKeyDown={(e) => {
                              const controlKeys = [
                                'Backspace',
                                'Delete',
                                'Tab',
                                'Enter',
                                'ArrowLeft',
                                'ArrowRight',
                                'ArrowUp',
                                'ArrowDown',
                                'Home',
                                'End',
                              ];
                              if (
                                e.ctrlKey ||
                                e.metaKey ||
                                controlKeys.includes(e.key)
                              ) {
                                return;
                              }
                              if (!/^[0-9]$/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e) => {
                              const text = e.clipboardData.getData('text');
                              if (!/^[0-9]+$/.test(text)) {
                                e.preventDefault();
                              }
                            }}
                            value={
                              input.suggested_supplier_net_price === ''
                                ? undefined
                                : suggestedNum
                            }
                            onChange={(val) =>
                              updateRoom(room.orderProductId, {
                                suggested_supplier_net_price:
                                  val === null || val === undefined
                                    ? ''
                                    : String(val),
                              })
                            }
                            addonAfter={roomCurrency}
                          />
                          {priceDiff !== null && priceDiff !== 0 && (
                            <div
                              className={`sap-price-diff${
                                priceDiff > 0 ? ' up' : ' down'
                              }`}
                            >
                              {priceDiff > 0 ? '↑' : '↓'} {priceDiff > 0 ? '+' : ''}
                              {priceDiff.toFixed(2)} {roomCurrency}{' '}
                              {t.fromExpected || 'from expected'}
                            </div>
                          )}
                        </div>
                      )}

                      <Checkbox
                        className="sap-different-check"
                        checked={isDifferent}
                        onChange={(e) =>
                          toggleDifferentPrice(room.orderProductId, e.target.checked)
                        }
                      >
                        {t.differentPrice || 'Different price?'}
                      </Checkbox>

                      {isDifferent && (
                        <button
                          type="button"
                          className="sap-link"
                          disabled={
                            input.suggested_supplier_net_price === '' ||
                            Number(input.suggested_supplier_net_price) <= 0
                          }
                          onClick={() => applyPriceToAll(room.orderProductId)}
                        >
                          {t.applyToAll || 'Apply to all rooms'}
                        </button>
                      )}
                    </div>

                    {/* Action */}
                    <div className="sap-cell sap-cell-action">
                      <div className="sap-cell-label">{t.colAction || 'Action'}</div>
                      <Button
                        className={`sap-btn-approve${isApproved ? ' active' : ''}`}
                        block
                        onClick={() => approveRoom(room.orderProductId)}
                      >
                        ✓{' '}
                        {isApproved
                          ? t.approved || 'Approved'
                          : t.approveRoom || 'Approve Room'}
                      </Button>
                      <Button
                        className={`sap-btn-reject${isNotApproved ? ' active' : ''}`}
                        block
                        onClick={() => rejectRoom(room.orderProductId)}
                      >
                        ✕ {t.notApproveBtn || 'Not Approve'}
                      </Button>
                      {isNotApproved && !isDifferent && (
                        <div className="sap-not-approved-hint">
                          {t.notApprovedReasonHint ||
                            'Please contact us to let us know the reason for not approving this room.'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}

          {/* Footer inside the card — aligned to the table grid so the total
              sits under the Net Price column and the button under Action. */}
          <div className="sap-grid sap-table-footer">
            <div className="sap-footer-hint">
              {hasPending && (
                <span className="sap-pending-hint">
                  {t.pendingHint ||
                    'Please review every room (approve or not approve) before submitting.'}
                </span>
              )}
            </div>
            <div className="sap-footer-total">
              <div className="sap-total">
                <span className="sap-total-label">
                  {t.totalExpectedNet || 'Total Expected Net'}
                </span>
                <span className="sap-total-value">
                  {totalExpectedNet.toFixed(2)} {currency}
                </span>
              </div>
            </div>
            <div className="sap-footer-submit">
              <Button
                type="primary"
                className="sap-submit"
                loading={submitting}
                disabled={submitDisabled}
                onClick={handleSubmit}
              >
                ➤ {t.submitAll || 'Submit All'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierApprovalPage;
